import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import api, { API_BASE, apiError } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import PlanGate from "@/app/PlanGate";
import { PageHeader, Btn, StatusBadge } from "@/app/ui";
import { Bot, Send, Sparkles, Check, X, Clock } from "lucide-react";

const SUGGESTIONS = [
  "Draft a polite reminder for my most overdue invoice",
  "Summarise how my business is doing this month",
  "Write a friendly follow-up for an open quote",
];

function AlfieInner() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const { data: actions, refetch: refetchActions } = useFetch("/alfie/actions");
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const msg = text || input;
    if (!msg.trim() || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API_BASE}/alfie/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      if (!res.ok) throw new Error("Alfie is unavailable");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + chunk }; return c; });
      }
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Sorry, I couldn't respond just now." }; return c; });
    }
    setStreaming(false);
  };

  const propose = async (action_type) => {
    try { await api.post("/alfie/propose", { action_type, payload: {} }); toast.success("Draft ready for approval below"); refetchActions(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const decide = async (id, approve) => {
    await api.post(`/alfie/actions/${id}/decide`, { approve });
    toast.success(approve ? "Approved" : "Rejected"); refetchActions();
  };

  const pending = (actions?.items || []).filter((a) => a.status === "pending_approval");

  return (
    <div className="animate-fade-up">
      <PageHeader title="Alfie AI" description="Your assistant drafts — you approve. Nothing sends without you." />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 amden-card flex flex-col h-[62vh]">
          <div className="flex-1 overflow-y-auto amden-scroll p-5 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <span className="mx-auto grid place-items-center h-14 w-14 rounded-2xl bg-navy text-white"><Bot size={26} /></span>
                <h3 className="mt-4 font-semibold text-navy">Hi, I'm Alfie</h3>
                <p className="text-slate-500 text-sm mt-1 max-w-sm mx-auto">Ask me to draft quotes, reminders or summaries. Try one of these:</p>
                <div className="mt-4 space-y-2 max-w-md mx-auto">
                  {SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)} data-testid="alfie-suggestion" className="w-full text-left text-sm px-4 py-2.5 rounded-lg border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-all text-slate-700">{s}</button>)}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`alfie-message-${m.role}`} className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-blue-600 text-white rounded-tr-sm" : "bg-slate-100 text-slate-800 rounded-tl-sm"}`}>
                  {m.content || <span className="text-slate-400">…</span>}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="border-t border-slate-100 p-3 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="alfie-input"
              placeholder="Ask Alfie anything…" className="flex-1 rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
            <Btn variant="accent" onClick={() => send()} disabled={streaming} data-testid="alfie-send"><Send size={16} /></Btn>
          </div>
        </div>

        <div className="space-y-4">
          <div className="amden-card p-5">
            <h3 className="font-semibold text-navy flex items-center gap-2"><Sparkles size={18} className="text-blue-accent" /> Quick drafts</h3>
            <div className="mt-3 space-y-2">
              <Btn variant="secondary" className="w-full justify-start" onClick={() => propose("draft_invoice_reminder")} data-testid="alfie-propose-reminder">Draft an invoice reminder</Btn>
              <Btn variant="secondary" className="w-full justify-start" onClick={() => propose("summarize_customer")} data-testid="alfie-propose-summary">Summarise a customer</Btn>
              <Btn variant="secondary" className="w-full justify-start" onClick={() => propose("draft_quote_email")}>Draft a quote email</Btn>
            </div>
          </div>
          <div className="amden-card p-5">
            <h3 className="font-semibold text-navy flex items-center gap-2"><Clock size={18} className="text-amber-500" /> Awaiting approval</h3>
            <div className="mt-3 space-y-3">
              {pending.length === 0 && <p className="text-sm text-slate-400">No pending drafts. Ask Alfie to draft something.</p>}
              {pending.map((a) => (
                <div key={a.id} className="rounded-lg border border-slate-200 p-3" data-testid={`alfie-action-${a.id}`}>
                  <p className="text-xs font-semibold text-slate-500 uppercase">{a.action_type.replace(/_/g, " ")}</p>
                  <p className="text-sm text-slate-700 mt-1 line-clamp-4 whitespace-pre-wrap">{a.draft}</p>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => decide(a.id, true)} data-testid={`alfie-approve-${a.id}`} className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm"><Check size={15} /> Approve</button>
                    <button onClick={() => decide(a.id, false)} className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm"><X size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Alfie() { return <PlanGate feature="alfie"><AlfieInner /></PlanGate>; }
